import type {
  IconSpec,
  ResolvedCommand,
  ResolvedMenu,
  ResolvedSubmenu,
  WhenContext,
} from '@hibro/shared';
import {
  evaluateWhen,
  isMenuCommandPlacement,
  isMenuSubmenuPlacement,
} from '@hibro/shared';

import { queryCommands, type TitleResolver } from './command-query';

/** A command row, resolved for display. */
export interface CommandMenuEntry {
  readonly kind: 'command';
  /** Command the row runs; also its React key, since a location dedupes by it. */
  readonly command: string;
  readonly moduleId: string;
  /**
   * The command's own title, category *not* prefixed. A menu location is
   * already a context ("things you can create"), so `Org Menu: Create
   * workspace` would state it twice; the palette's `label` exists for the flat
   * list, where nothing else says where an entry came from.
   */
  readonly title: string;
  /** The command's `description`, for faces that draw a secondary line. */
  readonly subtitle?: string;
  readonly icon?: IconSpec;
}

/** A nested submenu whose children have already been resolved. */
export interface SubmenuMenuEntry {
  readonly kind: 'submenu';
  readonly submenu: string;
  readonly moduleId: string;
  readonly title: string;
  readonly icon?: IconSpec;
  readonly children: readonly MenuEntry[];
}

/** One menu placement, resolved for display. */
export type MenuEntry = CommandMenuEntry | SubmenuMenuEntry;

export interface MenuLocationContents {
  readonly entries: MenuEntry[];
  /**
   * Placements naming a command **no module declares**. Kept separate from the
   * entries a `when` or `enablement` clause legitimately dropped: those are the
   * mechanism working, this is a broken manifest, and a caller with a log sink
   * should say so. Silently skipping both would make a typo look exactly like a
   * condition that did not hold.
   */
  readonly danglingCommands: readonly string[];
  /**
   * Placements naming a submenu nobody declared, or a cycle in the submenu
   * graph. Same reason as {@link danglingCommands}: a mistyped id must not
   * look like an empty folder.
   */
  readonly danglingSubmenus: readonly string[];
}

export interface MenuLocationOptions {
  /** Location key to gather, e.g. `MENU_LOCATION.headerCreate`. */
  readonly location: string;
  readonly menus: readonly ResolvedMenu[];
  /** Every known command, built-ins included. */
  readonly commands: readonly ResolvedCommand[];
  /**
   * Declared submenu placeholders. A placement's `submenu` id is looked up
   * here for the parent-row label / icon; missing ids become
   * {@link MenuLocationContents.danglingSubmenus}.
   */
  readonly submenus?: readonly ResolvedSubmenu[];
  /** Context both the placement's `when` and the command's `enablement` see. */
  readonly context: WhenContext;
  readonly resolveTitle: TitleResolver;
}

/**
 * What one menu location renders (架构方案 §5.3), ready for any face.
 *
 * The single authority on *which placements survive*, so two faces drawing two
 * locations can never disagree about what a menu entry is. It leans on
 * {@link queryCommands} for the command half rather than re-deriving it: that
 * function already owns listed-command skipping, `enablement` evaluation and
 * `%key%` resolution for the palette and the global search box, and a menu that
 * answered those questions its own way would be the same command judged
 * differently depending on where the user met it.
 *
 * A command that did not opt in with `internal: false` is hidden **here too**,
 * placement or not: the user never sees it, and a create menu the user can
 * click but cannot find by searching would be the box contradicting itself.
 *
 * The two gating axes stay separate, as the contract promises: `enablement`
 * (via `queryCommands`) applies wherever the command appears, `menu.when` only
 * to this placement.
 *
 * Submenu placements recurse with the submenu id as the next location. An
 * empty child list drops the parent row (same occupancy rule as "no Create
 * entries, no button"). A cycle or an undeclared submenu id is reported, not
 * rendered.
 */
export function resolveMenuLocation({
  location,
  menus,
  commands,
  submenus = [],
  context,
  resolveTitle,
}: MenuLocationOptions): MenuLocationContents {
  // No `mru`: recency ordering is right for a search box the user is typing
  // into, and wrong for a fixed menu, where a list that reshuffles under the
  // pointer costs more than it saves. Ordering here is declared (`order`).
  const visible = new Map(
    queryCommands({ commands, query: '', context, resolveTitle }).map(
      (candidate) => [candidate.id, candidate],
    ),
  );
  const declared = new Set(commands.map((command) => command.id));
  const submenuById = new Map(submenus.map((submenu) => [submenu.id, submenu]));

  return resolveLocation({
    location,
    menus,
    visible,
    declared,
    submenuById,
    context,
    resolveTitle,
    path: [],
  });
}

interface ResolveLocationOptions {
  readonly location: string;
  readonly menus: readonly ResolvedMenu[];
  readonly visible: ReadonlyMap<
    string,
    ReturnType<typeof queryCommands>[number]
  >;
  readonly declared: ReadonlySet<string>;
  readonly submenuById: ReadonlyMap<string, ResolvedSubmenu>;
  readonly context: WhenContext;
  readonly resolveTitle: TitleResolver;
  readonly path: readonly string[];
}

function resolveLocation({
  location,
  menus,
  visible,
  declared,
  submenuById,
  context,
  resolveTitle,
  path,
}: ResolveLocationOptions): MenuLocationContents {
  const placements = menus
    .filter(
      (menu) => menu.location === location && evaluateWhen(menu.when, context),
    )
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const entries: MenuEntry[] = [];
  const danglingCommands: string[] = [];
  const danglingSubmenus: string[] = [];
  const seenCommands = new Set<string>();
  const seenSubmenus = new Set<string>();
  const rankOf = new Map<string, number>();

  for (const placement of placements) {
    if (isMenuSubmenuPlacement(placement)) {
      if (seenSubmenus.has(placement.submenu)) {
        continue;
      }
      seenSubmenus.add(placement.submenu);
      if (path.includes(placement.submenu)) {
        danglingSubmenus.push(placement.submenu);
        continue;
      }
      const declaredSubmenu = submenuById.get(placement.submenu);
      if (declaredSubmenu === undefined) {
        danglingSubmenus.push(placement.submenu);
        continue;
      }
      const nested = resolveLocation({
        location: placement.submenu,
        menus,
        visible,
        declared,
        submenuById,
        context,
        resolveTitle,
        path: [...path, placement.submenu],
      });
      danglingCommands.push(...nested.danglingCommands);
      danglingSubmenus.push(...nested.danglingSubmenus);
      if (nested.entries.length === 0) {
        continue;
      }
      const title = resolveTitle(
        declaredSubmenu.moduleId,
        declaredSubmenu.label,
      );
      const key = `submenu:${placement.submenu}`;
      rankOf.set(key, placement.order ?? 0);
      entries.push({
        kind: 'submenu',
        submenu: placement.submenu,
        moduleId: declaredSubmenu.moduleId,
        title,
        ...(declaredSubmenu.icon === undefined
          ? {}
          : { icon: declaredSubmenu.icon }),
        children: nested.entries,
      });
      continue;
    }

    if (!isMenuCommandPlacement(placement)) {
      continue;
    }
    if (seenCommands.has(placement.command)) {
      // Two modules pointed at the same command. One row is what both meant,
      // and the id is the face's React key.
      continue;
    }
    if (!declared.has(placement.command)) {
      danglingCommands.push(placement.command);
      seenCommands.add(placement.command);
      continue;
    }
    const candidate = visible.get(placement.command);
    if (!candidate) {
      // Declared but not user-visible: not opted in, or `enablement` said no.
      seenCommands.add(placement.command);
      continue;
    }
    seenCommands.add(placement.command);
    rankOf.set(`command:${candidate.id}`, placement.order ?? 0);
    entries.push({
      kind: 'command',
      command: candidate.id,
      moduleId: candidate.moduleId,
      title: candidate.title,
      ...(candidate.description === undefined
        ? {}
        : { subtitle: candidate.description }),
      ...(candidate.icon === undefined ? {} : { icon: candidate.icon }),
    });
  }

  // Stable tie-break so equal `order` values do not leave the order to module
  // load order — which varies with activation and would visibly reshuffle.
  entries.sort((a, b) => {
    const rankA = rankOf.get(entryRankKey(a)) ?? 0;
    const rankB = rankOf.get(entryRankKey(b)) ?? 0;
    return rankA === rankB ? a.title.localeCompare(b.title) : rankA - rankB;
  });

  return { entries, danglingCommands, danglingSubmenus };
}

function entryRankKey(entry: MenuEntry): string {
  return entry.kind === 'command'
    ? `command:${entry.command}`
    : `submenu:${entry.submenu}`;
}
